import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionStorageService } from '../session-storage.service';
import { LocalStorageKey } from '../models/storageKey';
import { CanComponentDeactivate } from '../guards/can-deactivate.guard';
import { UrlConfigService } from '../url-config.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { FunnelTrackingService } from '../analytics/funnel-tracking.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnDestroy, CanComponentDeactivate {
  email = '';
  locale = 'en-US';
  agreeTerms = false;
  businessName = '';
  formModified = false;
  portalUrl: string;
  termsUrl: string;
  privacyUrl: string;
  eSignUrl: string;

  // Business Contact Info fields
  contactPerson = '';
  contactPhoneNumber = '';
  contactEmail = '';
  sameAsLoginEmail = false;
  streetAddress = '';
  city = '';
  stateProvince = '';
  postalCode = '';
  country = 'US';

  // US States
  usStates = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' }
  ];

  // Canadian Provinces
  caProvinces = [
    { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' }, { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' }, { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' }, { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' }, { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' }, { code: 'YT', name: 'Yukon' }
  ];

  private storageSubscription?: Subscription;
  // Funnel milestone: fired once when the Business Contact Info section first
  // becomes complete (splits step-1 drop-off in GA between "gave up before
  // contact details" and "gave up at the end").
  private contactInfoCompletedTracked = false;
  private emailEnteredTracked = false;

  constructor(
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private urlConfig: UrlConfigService,
    private analytics: AnalyticsService,
    private funnelTracking: FunnelTrackingService
  ) {
    this.portalUrl = this.urlConfig.getPortalUrl();
    this.termsUrl = this.urlConfig.getTermsAndConditionsUrl();
    this.privacyUrl = this.urlConfig.getPrivacyPolicyUrl();
    this.eSignUrl = this.urlConfig.getESignConsentUrl();

    // Prefill reactively: the session doc restore is async (Firestore local
    // cache), so keep applying stored values until the user starts typing.
    this.storageSubscription = this.sessionStorageService.storage$.subscribe(storage => {
      if (this.formModified) {
        return;
      }
      this.email = storage[LocalStorageKey.USER_EMAIL] || this.email;
      this.locale = storage[LocalStorageKey.USER_LOCALE] || this.locale;
      this.agreeTerms = storage[LocalStorageKey.AGREE_TERMS] === true || this.agreeTerms;
      this.businessName = storage[LocalStorageKey.VENUE_NAME] || this.businessName;
      this.contactPerson = storage[LocalStorageKey.CONTACT_PERSON] || this.contactPerson;
      this.contactPhoneNumber = storage[LocalStorageKey.CONTACT_PHONE_NUMBER] || this.contactPhoneNumber;
      this.contactEmail = storage[LocalStorageKey.CONTACT_EMAIL] || this.contactEmail;
      this.streetAddress = storage[LocalStorageKey.STREET_ADDRESS] || this.streetAddress;
      this.city = storage[LocalStorageKey.CITY] || this.city;
      this.stateProvince = storage[LocalStorageKey.STATE_PROVINCE] || this.stateProvince;
      this.postalCode = storage[LocalStorageKey.POSTAL_CODE] || this.postalCode;
      this.country = storage[LocalStorageKey.COUNTRY] || this.country || this.getDefaultCountry();

      // A restored session that already has the contact section complete
      // reached this milestone in an earlier visit — don't re-fire it on the
      // next keystroke.
      if (this.isContactInfoComplete()) {
        this.contactInfoCompletedTracked = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.storageSubscription?.unsubscribe();
  }

  getDefaultCountry(): string {
    return this.locale.endsWith('-CA') ? 'CA' : 'US';
  }

  getStatesForCountry(): { code: string; name: string }[] {
    return this.country === 'CA' ? this.caProvinces : this.usStates;
  }

  onCountryChange(): void {
    this.stateProvince = '';
    this.onFieldChange();
  }

  onSameAsLoginEmailChange(): void {
    if (this.sameAsLoginEmail) {
      this.contactEmail = this.email;
    }
    this.onFieldChange();
  }

  // signup_started, step views, and page timing are handled by
  // FunnelTrackingService on route navigation.

  private isContactInfoComplete(): boolean {
    return !!(
      this.contactPerson.trim() &&
      this.contactPhoneNumber.trim() &&
      (this.sameAsLoginEmail || this.contactEmail.trim()) &&
      this.streetAddress.trim() &&
      this.city.trim() &&
      this.stateProvince &&
      this.postalCode.trim()
    );
  }

  /** Everything on the page is required, including the full contact block. */
  missingRequiredFields(): string[] {
    const missing: string[] = [];
    if (!this.email.trim()) missing.push('Email address');
    if (!this.businessName.trim()) missing.push('Business name');
    if (!this.contactPerson.trim()) missing.push('Contact person');
    if (!this.contactPhoneNumber.trim()) missing.push('Phone number');
    if (!this.sameAsLoginEmail && !this.contactEmail.trim()) missing.push('Contact email');
    if (!this.streetAddress.trim()) missing.push('Street address');
    if (!this.city.trim()) missing.push('City');
    if (!this.stateProvince) missing.push('State/Province');
    if (!this.postalCode.trim()) missing.push('Postal code');
    if (!this.agreeTerms) missing.push('Terms agreement');
    return missing;
  }

  get isFormComplete(): boolean {
    return this.missingRequiredFields().length === 0;
  }

  onFieldChange() {
    this.formModified = true;
    
    // Track when email is entered for the first time. The session doc only
    // gets userEmail at submit, so a per-visit flag is what keeps this from
    // re-firing on every subsequent field change.
    if (!this.emailEnteredTracked && this.email && this.email.includes('@') && !this.sessionStorageService.getUserEmail()) {
      this.emailEnteredTracked = true;
      const emailDomain = this.email.split('@')[1];
      this.analytics.trackEvent('email_entered', {
        email_domain: emailDomain,
        locale: this.locale,
        time_to_enter_ms: this.funnelTracking.getFunnelElapsedMs()
      });
    }

    // Funnel milestone: the contact section just became complete
    if (!this.contactInfoCompletedTracked && this.isContactInfoComplete()) {
      this.contactInfoCompletedTracked = true;
      this.analytics.trackEvent('contact_info_completed', {
        country: this.country,
        same_as_login_email: this.sameAsLoginEmail,
        time_to_complete_ms: this.funnelTracking.getFunnelElapsedMs()
      });
    }
  }

  canDeactivate(): boolean {
    if (this.formModified) {
      const shouldLeave = confirm(
        'Are you sure you want to navigate to the review page? Progress will not be saved unless the continue button is clicked.'
      );
      
      // Track navigation warning
      this.analytics.trackEvent('navigation_warning_shown', {
        from_page: 'login',
        action_taken: shouldLeave ? 'left' : 'stayed'
      });
      
      return shouldLeave;
    }
    return true;
  }

  submitForm() {
    // Validate form. No password is collected — the user sets up credentials
    // in the portal via the invite email sent after venue creation. Existing
    // BlazeBite emails are allowed; the invite handles both cases.
    const missing = this.missingRequiredFields();
    if (missing.length > 0) {
      this.analytics.trackFormError('multiple_fields', 'required_fields_missing', 'login',
        `Missing required fields: ${missing.join(', ')}`);
      alert(`Please complete the following before continuing: ${missing.join(', ')}.`);
      return;
    }
    this.email = this.email.trim();

    // Track terms acceptance
    if (this.agreeTerms) {
      this.analytics.trackEvent('terms_accepted', {
        agreement_type: 'all_terms',
        time_to_accept_ms: this.funnelTracking.getFunnelElapsedMs(),
        locale: this.locale
      });
    }

    // Track signup step completion
    this.funnelTracking.trackStepCompleted('login', {
      business_name_length: this.businessName.length,
      locale: this.locale
    });

    // Set user properties
    this.analytics.setUserProperties({
      user_locale: this.locale,
      email_domain: this.email.split('@')[1],
      signup_step: 'login_completed'
    });

    // Save to session storage
    this.sessionStorageService.setUserEmail(this.email);
    this.sessionStorageService.setUserLocale(this.locale);
    this.sessionStorageService.setAgreeTerms(this.agreeTerms);
    this.sessionStorageService.setBusinessName(this.businessName);

    // Save contact info
    this.sessionStorageService.setItem(LocalStorageKey.CONTACT_PERSON, this.contactPerson);
    this.sessionStorageService.setItem(LocalStorageKey.CONTACT_PHONE_NUMBER, this.contactPhoneNumber);
    this.sessionStorageService.setItem(LocalStorageKey.CONTACT_EMAIL, this.sameAsLoginEmail ? this.email : this.contactEmail);
    this.sessionStorageService.setItem(LocalStorageKey.STREET_ADDRESS, this.streetAddress);
    this.sessionStorageService.setItem(LocalStorageKey.CITY, this.city);
    this.sessionStorageService.setItem(LocalStorageKey.STATE_PROVINCE, this.stateProvince);
    this.sessionStorageService.setItem(LocalStorageKey.POSTAL_CODE, this.postalCode);
    this.sessionStorageService.setItem(LocalStorageKey.COUNTRY, this.country);

    this.sessionStorageService.setItem(LocalStorageKey.LOGIN, 'complete');

    this.formModified = false;
    this.router.navigate(['/venueType']);
  }
}